import React, { useContext, useMemo, useState } from 'react';
import { AppContext } from '@/contexts/AppContext';
import type { CategoryInput } from '@/types/api';

interface CategoryFormState {
  id?: number;
  name: string;
  slug: string;
  description: string;
  isActive: boolean;
}

const createEmptyCategoryForm = (): CategoryFormState => ({
  id: undefined,
  name: '',
  slug: '',
  description: '',
  isActive: true,
});

const CategoriesManagement: React.FC = () => {
  const context = useContext(AppContext);

  if (!context) {
    throw new Error('CategoriesManagement deve ser usado dentro de AppProvider');
  }

  const {
    adminCategories,
    createCategory,
    updateCategory,
    toggleCategoryStatus,
    deleteCategory,
    loadAdminCategories,
  } = context;

  const [formState, setFormState] = useState<CategoryFormState | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filterActiveOnly, setFilterActiveOnly] = useState(false);

  const categoriesToDisplay = useMemo(() => {
    return filterActiveOnly ? adminCategories.filter(category => category.is_active) : adminCategories;
  }, [adminCategories, filterActiveOnly]);

  const startCreating = () => {
    setFormState(createEmptyCategoryForm());
    setFeedback(null);
    setError(null);
  };

  const startEditing = (categoryId: number) => {
    const category = adminCategories.find(item => item.id === categoryId);
    if (!category) return;

    setFormState({
      id: category.id,
      name: category.name,
      slug: category.slug,
      description: category.description ?? '',
      isActive: category.is_active,
    });
    setFeedback(null);
    setError(null);
  };

  const cancelEditing = () => {
    setFormState(null);
    setFeedback(null);
    setError(null);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormState(prev => (prev ? { ...prev, [name]: value } : prev));
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setFormState(prev => (prev ? { ...prev, isActive: checked } : prev));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!formState) return;

    const payload: CategoryInput = {
      name: formState.name.trim(),
      slug: formState.slug.trim() || undefined,
      description: formState.description.trim() || undefined,
      is_active: formState.isActive,
    };

    if (!payload.name) {
      setError('Informe um nome para a categoria.');
      return;
    }

    setSubmitting(true);
    setError(null);
    setFeedback(null);

    try {
      if (formState.id) {
        await updateCategory(formState.id, payload);
        setFeedback('Categoria atualizada com sucesso.');
      } else {
        await createCategory(payload);
        setFeedback('Categoria criada com sucesso.');
      }
      setFormState(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao salvar categoria.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (categoryId: number) => {
    const confirmation = window.confirm('Tem certeza que deseja excluir esta categoria?');
    if (!confirmation) return;

    setSubmitting(true);
    setError(null);
    setFeedback(null);
    try {
      await deleteCategory(categoryId);
      setFeedback('Categoria removida com sucesso.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao remover categoria (verifique se há produtos vinculados).';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (categoryId: number, isActive: boolean) => {
    setSubmitting(true);
    setError(null);
    setFeedback(null);
    try {
      await toggleCategoryStatus(categoryId);
      setFeedback(`Categoria ${isActive ? 'desativada' : 'ativada'} com sucesso.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao alterar status da categoria.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefresh = async () => {
    setSubmitting(true);
    try {
      await loadAdminCategories(filterActiveOnly);
      setFeedback('Categorias atualizadas.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erro ao recarregar categorias.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Gerenciamento de Categorias</h2>
        <div className="section-actions">
          <label className="filter-toggle">
            <input
              type="checkbox"
              checked={filterActiveOnly}
              onChange={event => setFilterActiveOnly(event.target.checked)}
              disabled={submitting}
            />
            <span>Apenas ativas</span>
          </label>
          <button
            type="button"
            className="btn-secondary"
            onClick={handleRefresh}
            disabled={submitting}
          >
            Recarregar
          </button>
          <button
            type="button"
            className="add-button"
            onClick={startCreating}
            disabled={submitting}
          >
            Nova categoria
          </button>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {feedback && <p className="success">{feedback}</p>}

      {formState && (
        <form onSubmit={handleSubmit} className="admin-form admin-category-form">
          <h3>{formState.id ? 'Editar categoria' : 'Nova categoria'}</h3>
          <label>
            Nome
            <input
              name="name"
              value={formState.name}
              onChange={handleChange}
              required
              disabled={submitting}
            />
          </label>
          <label>
            Descrição (opcional)
            <textarea
              name="description"
              value={formState.description}
              onChange={handleChange}
              rows={3}
              disabled={submitting}
            />
          </label>
          <label>
            Slug (opcional)
            <input
              name="slug"
              value={formState.slug}
              onChange={handleChange}
              placeholder="ex: petiscos-naturais"
              disabled={submitting}
            />
          </label>
          {formState.id && (
            <label>
              Ativa
              <input
                type="checkbox"
                checked={formState.isActive}
                onChange={handleCheckboxChange}
                disabled={submitting}
              />
            </label>
          )}
          <div className="form-actions">
            <button type="submit" className="save-button" disabled={submitting}>
              {formState.id ? 'Atualizar' : 'Criar'}
            </button>
            <button type="button" className="cancel-button" onClick={cancelEditing} disabled={submitting}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      {categoriesToDisplay.length === 0 ? (
        <p>Nenhuma categoria cadastrada.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nome</th>
              <th>Slug</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {categoriesToDisplay.map(category => (
              <tr key={category.id}>
                <td>{category.id}</td>
                <td>{category.name}</td>
                <td>{category.slug}</td>
                <td>{category.is_active ? 'Ativa' : 'Inativa'}</td>
                <td className="actions">
                  <div className="admin-actions">
                    <button
                      type="button"
                      className="icon-button icon-button--edit"
                      onClick={() => startEditing(category.id)}
                      disabled={submitting}
                      title="Editar categoria"
                    >
                      ✏️
                      <span className="sr-only">Editar</span>
                    </button>
                    <button
                      type="button"
                      className={`icon-button icon-button--toggle ${category.is_active ? 'is-active' : 'is-inactive'}`}
                      onClick={() => handleToggleStatus(category.id, category.is_active)}
                      disabled={submitting}
                      title={category.is_active ? 'Desativar categoria' : 'Ativar categoria'}
                    >
                      {category.is_active ? '⏸' : '▶'}
                      <span className="sr-only">
                        {category.is_active ? 'Desativar' : 'Ativar'}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="icon-button icon-button--delete"
                      onClick={() => handleDelete(category.id)}
                      disabled={submitting}
                      title="Excluir categoria"
                    >
                      🗑️
                      <span className="sr-only">Excluir</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default CategoriesManagement;
